import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/safeBrowser';
import { mockData } from '@/data/mockData';
import { fetchAndTransformCRMData } from '@/data/crmAdapter';

type DataSource = 'simulated' | 'crm';

interface DataSourceContextType {
  dataSource: DataSource;
  setDataSource: (source: DataSource) => void;
  hasCRMConnection: boolean;
  hasCRMData: boolean;
  isLoading: boolean;
  isDataReady: boolean;
  dataVersion: number;
  handleCRMDisconnect: () => void;
  handleCRMConnect: () => void;
}

const DataSourceContext = createContext<DataSourceContextType>({
  dataSource: 'simulated',
  setDataSource: () => {},
  hasCRMConnection: false,
  hasCRMData: false,
  isLoading: true,
  isDataReady: true,
  dataVersion: 0,
  handleCRMDisconnect: () => {},
  handleCRMConnect: () => {},
});

export function DataSourceProvider({ children }: { children: ReactNode }) {
  const { organization } = useAuth();
  const orgId = organization?.id ?? null;
  const [dataSource, setDataSourceState] = useState<DataSource>('simulated');
  const [hasCRMConnection, setHasCRMConnection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);
  const lastCheckedOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }

    // Skip if we already checked CRM for this exact org
    if (lastCheckedOrgIdRef.current === orgId) {
      return;
    }

    // Reset loading state on org switch to force skeletons
    setIsLoading(true);
    lastLoadedOrgIdRef.current = null;

    const checkCRM = async () => {
      try {
        const { data } = await supabase
          .from('crm_connections')
          .select('id')
          .eq('organization_id', orgId)
          .eq('status', 'connected')
          .limit(1);

        const connected = !!(data && data.length > 0);
        setHasCRMConnection(connected);

        // Load saved preference
        const saved = safeGetItem(`data-source-${orgId}`);
        if (saved === 'crm' && connected) {
          setDataSourceState('crm');
        } else if (saved === 'simulated') {
          setDataSourceState('simulated');
        } else {
          // Default: CRM if connected, otherwise simulated
          setDataSourceState(connected ? 'crm' : 'simulated');
        }
      } catch {
        setHasCRMConnection(false);
        setDataSourceState('simulated');
      } finally {
        lastCheckedOrgIdRef.current = orgId;
        setIsLoading(false);
      }
    };

    checkCRM();
  }, [orgId]);

  // Track the last orgId for which data was fully loaded to avoid redundant fetches
  const lastLoadedOrgIdRef = useRef<string | null>(null);

  // When dataSource changes, load the appropriate data
  const loadData = useCallback(async () => {
    if (!orgId) return;
    
    if (dataSource === 'crm') {
      // Skip if CRM data for this org is already loaded
      const alreadyLoaded = lastLoadedOrgIdRef.current === orgId && mockData.isCRM;
      if (!alreadyLoaded && !mockData.isCRM) {
        setIsDataReady(false);
      }
      try {
        if (alreadyLoaded) {
          setIsDataReady(true);
          return;
        }
        console.log('[DataSource] Loading CRM data...');
        const crmData = await fetchAndTransformCRMData(orgId);
        mockData.injectCRMData(crmData);
        lastLoadedOrgIdRef.current = orgId;
        setDataVersion(v => v + 1);
        console.log('[DataSource] CRM data loaded successfully');
      } catch (err) {
        console.error('[DataSource] Failed to load CRM data, keeping CRM source without fake fallback:', err);

        if (!mockData.isCRM) {
          mockData.injectCRMData({
            salesReps: [],
            accounts: [],
            contacts: [],
            leads: [],
            opportunities: [],
            activities: [],
            funnels: [],
          });
        }

        setDataVersion(v => v + 1);
      } finally {
        setIsDataReady(true);
      }
    } else {
      if (mockData.isCRM) {
        mockData.revertToMock();
        lastLoadedOrgIdRef.current = null;
        setDataVersion(v => v + 1);
      }
      setIsDataReady(true);
    }
  }, [dataSource, orgId]);

  useEffect(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  // Realtime subscription: reload CRM data when webhook updates DB
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (dataSource !== 'crm' || !orgId || isLoading) return;

    const REALTIME_TABLES = [
      'crm_synced_deals',
      'crm_synced_contacts',
      'crm_synced_organizations',
      'crm_synced_deal_products',
      'crm_synced_products',
      'crm_synced_activities',
      'crm_synced_tasks',
    ] as const;

    const channel = supabase.channel('crm-realtime-changes');

    for (const table of REALTIME_TABLES) {
      channel.on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table, filter: `organization_id=eq.${orgId}` },
        () => {
          // Debounce: wait 3s after last event before reloading
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            console.log('[DataSource] Realtime change detected, reloading CRM data...');
            loadData();
          }, 3000);
        }
      );
    }

    channel.subscribe();

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [dataSource, orgId, isLoading, loadData]);

  const setDataSource = (source: DataSource) => {
    setDataSourceState(source);
    if (orgId) {
      safeSetItem(`data-source-${orgId}`, source);
    }
  };

  const handleCRMDisconnect = useCallback(() => {
    console.log('[DataSource] CRM disconnected — clearing all in-memory data');
    mockData.revertToMock();
    lastLoadedOrgIdRef.current = null;
    lastCheckedOrgIdRef.current = null;
    setDataSourceState('simulated');
    setHasCRMConnection(false);
    setIsDataReady(true);
    if (orgId) {
      safeRemoveItem(`data-source-${orgId}`);
    }
    setDataVersion(v => v + 1);
  }, [orgId]);

  const handleCRMConnect = useCallback(() => {
    console.log('[DataSource] CRM connected — switching to CRM data source');
    lastLoadedOrgIdRef.current = null; // Force reload
    setHasCRMConnection(true);
    setDataSourceState('crm');
    if (orgId) {
      safeSetItem(`data-source-${orgId}`, 'crm');
    }
    // loadData will be triggered by the dataSource change via useEffect
  }, [orgId]);

  const hasCRMData = dataSource === 'crm' && isDataReady && (
    mockData.opportunities.length > 0 || mockData.leads.length > 0 || mockData.activities.length > 0 ||
    mockData.accounts.length > 0 || mockData.contacts.length > 0 || mockData.salesReps.length > 0 ||
    mockData.funnels.length > 0
  );

  return (
    <DataSourceContext.Provider value={{ dataSource, setDataSource, hasCRMConnection, hasCRMData, isLoading, isDataReady, dataVersion, handleCRMDisconnect, handleCRMConnect }}>
      {children}
    </DataSourceContext.Provider>
  );
}

export function useDataSource() {
  return useContext(DataSourceContext);
}
